import React, { FC, useEffect, useState } from "react";
import { useAppSelector } from "index";
import { Card, CardContent, CardHeader, Grid, Paper, Typography, useTheme } from "@mui/material";
import * as d3 from "d3";
import { ApplicationState } from "@store/index";
import { Category, DataPoint } from "fileParsing/types";
import SparkLine from "./SparkLine";
import Node from "containers/Sidebar/Node";
import NoDataComponent from "containers/Components/NoDataComponent";
import { useDatapointsSelectionFiltered } from "containers/hooks/useData";

export type VisualizationSettings = {
  width: number;
  height: number;
  spacing: number;
  cardHeight: number;
  bins: d3.TimeInterval;
};

export const VisualizationSettingsDefaults: VisualizationSettings = {
  width: 1024,
  height: 140,
  spacing: 8,
  cardHeight: 320,
  bins: d3.timeMonth.every(3) || d3.timeMonth,
};

type CategorizedBin = {
  values: { [key: string]: number };
  date: Date;
};

// Component-specific props.
type ComponentProps = {
  //data: ProviderFile[],
  settings: Partial<VisualizationSettings>;
};

const SparkLines: FC<ComponentProps> = (props: ComponentProps) => {
  const items = useAppSelector((state: ApplicationState) => state.data.exports);
  const theme = useTheme();
  const data = useDatapointsSelectionFiltered();

  const [subCategories, setSubCategories] = useState<{
    [key in Category]?: Set<string>;
  }>({});
  const [binsCategorized, setBinsCategorized] = useState<CategorizedBin[]>([]);
  const settings = { ...VisualizationSettingsDefaults, ...props };

  useEffect(() => {
    updateData();
  }, [items.length, data.length, settings.bins]);

  function updateData() {
    const [minDate, maxDate] = d3.extent(data, (d: DataPoint) => d.date);

    // setup x scale
    const xScale = d3
      .scaleTime()
      .domain([minDate || new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), maxDate || new Date()]);

    // define histogram generator
    const generator = d3
      .bin<DataPoint, Date>()
      .value((d) => d.date)
      .thresholds(xScale.ticks(settings.bins));

    // generate bins
    const bins = generator(data);

    // count data points per subcategory in each bin
    const generatedBinsCategorized: CategorizedBin[] = bins.map((b) => ({
      values: b.reduce(
        (acc: Record<string, number>, val: DataPoint) =>
          val.subcategory in acc
            ? { ...acc, [val.subcategory]: acc[val.subcategory] + 1 }
            : { ...acc, [val.subcategory]: 1 },
        {},
      ),
      date: b.x0 && b.x1 ? new Date(b.x0.getTime() + (b.x1.getTime() - b.x0.getTime()) / 2) : (b.x0 as Date),
    }));

    const generatedSubCategories = data.reduce(
      (acc: Record<string, Set<string>>, d: DataPoint) => ({
        ...acc,
        [d.category]: acc[d.category] !== undefined ? acc[d.category].add(d.subcategory) : new Set([d.subcategory]),
      }),
      {},
    );

    setBinsCategorized(generatedBinsCategorized);
    setSubCategories(generatedSubCategories);
  }

  return (
    <>
      {data.length === 0 && <NoDataComponent />}
      <Grid
        container
        direction="row"
        justifyContent="stretch"
        spacing={2}
        alignItems="center"
        sx={{
          padding: "5%",
        }}
      >
        {Object.values(Category).map((cat: Category, index: number) => (
          <Grid
            item
            key={index}
            xs={12}
            md={6}
            lg={4}
            sx={{
              alignSelf: "stretch",
              width: "100%",
            }}
          >
            <Card
              style={{
                width: "100%",
                height: "100%",
                maxHeight: `${settings.cardHeight}px`,
              }}
            >
              <CardHeader
                avatar={<Node color={theme.palette.categories[cat].main} size={20}></Node>}
                title={<Typography>{cat}</Typography>}
                style={{ whiteSpace: "nowrap", overflow: "hidden" }}
              />
              <CardContent
                style={{
                  height: "100%",
                  overflowY: "auto",
                }}
              >
                {subCategories[cat] === undefined ? (
                  <Typography>No data</Typography>
                ) : (
                  Array.from(subCategories[cat] || []).map((sc: string, index: number) => (
                    <Paper
                      key={index}
                      elevation={3}
                      style={{
                        padding: 1,
                        marginBottom: 3,
                        wordWrap: "break-word",
                      }}
                    >
                      <Typography variant="subtitle2">
                        {sc} ({binsCategorized.reduce((sum, c) => sum + (c.values[sc] || 0), 0)})
                      </Typography>
                      <SparkLine
                        data={binsCategorized.map((c) => ({
                          value: c.values[sc] || 0,
                          date: c.date,
                        }))}
                        settings={{
                          lineColor: theme.palette.categories[cat].main,
                          chartHeight: settings.cardHeight / 20,
                        }}
                      />
                    </Paper>
                  ))
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default SparkLines;
